import { useNavigate } from "react-router-dom";
import { ExternalLink, FileText, Building2, BookOpen, Shirt, MessageCircle, HelpCircle, Ban, ClipboardList, Search, Link2, CalendarCheck } from "lucide-react";
import { Button } from "../ui/button";

export function Card({ title, icon: Icon, children, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Icon className="w-3.5 h-3.5 text-primary" /> {title}</p>
      <div className="mt-2 text-sm text-slate-700 space-y-1.5">{children}</div>
    </div>
  );
}

const Bullets = ({ items }) => <ul className="list-disc pl-4 space-y-1">{items.map((x) => <li key={x}>{x}</li>)}</ul>;

export function OfficialResources({ target, california, phase }) {
  if (target && !target.id) target = null;
  const dept = [
    target?.careers_url && ["Department careers page", target.careers_url, `${target.name} — hiring and recruitment`],
    target?.official_site && ["Official department website", target.official_site, target.name],
  ].filter(Boolean);
  const general = [
    ...(phase === "board_prep" || phase === "board" || phase === "chief" ? [] : [
      california && ["Entry testing — FCTC", "https://www.fctconline.org/", "California Firefighter Candidate Testing Center: written test and Statewide Eligibility List"],
      ["Physical ability test — CPAT", "https://www.iaff.org/cpat/", "IAFF/IAFC Candidate Physical Ability Test"],
      ["EMT certification — NREMT", "https://www.nremt.org/", "National Registry of Emergency Medical Technicians"],
      ["Federal fire jobs — USAJOBS", "https://www.usajobs.gov/", "Federal wildland and structural fire positions"],
    ]),
  ].filter(Boolean);
  const Row = ([label, url, sub]) => (
    <li key={url}>
      <a href={url} target="_blank" rel="noopener noreferrer nofollow" className="flex items-start justify-between gap-3 rounded-lg px-3 py-2 hover:bg-slate-50 group" data-testid={`resource-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
        <span className="min-w-0"><span className="block text-sm font-semibold text-slate-800 group-hover:text-primary">{label}</span>{sub && <span className="block text-xs text-slate-500 truncate">{sub}</span>}</span>
        <ExternalLink className="w-3.5 h-3.5 text-slate-400 mt-1 shrink-0" />
      </a>
    </li>
  );
  if (!dept.length && !general.length) return null;
  return (
    <section className="rounded-xl border border-slate-200 bg-white" data-testid="official-resources">
      <p className="px-4 pt-4 text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5 text-primary" /> Official resources</p>
      {dept.length > 0 && <><p className="px-4 mt-3 text-[10px] font-bold uppercase tracking-wider text-slate-400">{target.name}</p><ul className="px-1 mt-1">{dept.map(Row)}</ul></>}
      {!target && <p className="px-4 mt-2 text-xs text-slate-500" data-testid="official-resources-no-target">Choose a target department to see its careers page and official site here.</p>}
      {general.length > 0 && <><p className="px-4 mt-3 text-[10px] font-bold uppercase tracking-wider text-slate-400">Testing & certification</p><ul className="px-1 mt-1 pb-2">{general.map(Row)}</ul></>}
      <p className="px-4 pb-4 pt-1 text-[11px] text-slate-500">Official sources only. Requirements and deadlines change — confirm on the department's own page before you rely on anything here.</p>
    </section>
  );
}

export function ApplicationGuide() {
  const navigate = useNavigate();
  return (
    <div className="grid md:grid-cols-3 gap-3" data-testid="application-guide">
      <Card title="Resume" icon={FileText} testid="app-guide-resume">
        <p>Build it once in the Resume Builder, then tailor the summary line for each department.</p>
        <Button size="sm" variant="outline" onClick={() => navigate("/resume")} data-testid="app-guide-resume-btn">Open Resume Builder</Button>
      </Card>
      <Card title="Documents to gather" icon={ClipboardList} testid="app-guide-docs">
        <Bullets items={["Certification cards (EMT/EMR, FF I/II, CPAT card if you have one)", "Driver's license & DMV record", "Diploma / transcripts", "Work history with dates & supervisors", "References"]} />
      </Card>
      <Card title="Submit & track" icon={Search} testid="app-guide-track">
        <p>Apply only through the department's or government hiring portal. Keep one sheet: department, deadline, written date, PAT date, board date, status.</p>
      </Card>
    </div>
  );
}

export function StationVisitGuide({ target }) {
  const navigate = useNavigate();
  if (target && !target.id) target = null;
  const s = target?.study;
  return (
    <div className="space-y-3" data-testid="station-guide">
      <div className="grid md:grid-cols-2 gap-3">
        <Card title="Research first" icon={BookOpen} testid="sv-guide-research">
          <p>Know the department before you knock: stations, apparatus, services, the community and what they've said they value.</p>
          {target ? <p className="text-xs text-slate-500">Your progress on {target.name}: {s?.cards_seen || 0} cards reviewed · {s?.answered || 0} quiz answers{s?.answered ? ` (${Math.round((s.correct / s.answered) * 100)}% correct)` : ""}.</p> : null}
          <Button size="sm" variant="outline" onClick={() => navigate(target ? `/study/departments/${target.id}` : "/study/departments")} data-testid="sv-guide-research-btn"><Building2 className="w-4 h-4 mr-1" /> {target ? `Study ${target.name}` : "Open Department Research"}</Button>
        </Card>
        <Card title="Visit policy · when & how" icon={CalendarCheck} testid="sv-guide-when">
          <Bullets items={["Check the department's visit policy first — some schedule visits, some limit them during recruitment. Respect the answer.", "Mid-morning or early afternoon; never during shift change, meals or an obvious emergency.", "Call ahead or ask at the front — say who you are and that you're a candidate preparing to apply.", "Keep it to 30–45 minutes unless they invite you to stay."]} />
        </Card>
        <Card title="What to wear & how to introduce yourself" icon={Shirt} testid="sv-guide-dress">
          <Bullets items={["Slacks and a collared shirt at minimum; clean and well-groomed; no hat, no sunglasses indoors. Suits are for chief's interviews.", "Firm handshake, eye contact: your name, that you're preparing to apply, and one sentence on why this department.", "Bring a resume in case they ask. A small treat for the crew (coffee, donuts) is a nice touch where appropriate."]} />
        </Card>
        <Card title="Questions worth asking" icon={MessageCircle} testid="sv-guide-questions">
          <Bullets items={["What do you look for in a candidate — and what sinks people?", "What does a probationary year look like here?", "What are the busiest call types in this district?", "How does the department handle EMS — transport, ALS, community programs?", "What would you tell someone preparing for your oral board?"]} />
        </Card>
      </div>
      <Card title="Etiquette · don't" icon={Ban} testid="sv-guide-dont">
        <Bullets items={["Show up unannounced during meals, training or a call — and don't linger if the crew gets busy.", "Touch apparatus or equipment, wander the station, or bring friends.", "Ask about pay, days off or 'how easy is the test'.", "Treat it casually: everything you do is remembered, and crews talk to the board. Thank them and remember names."]} />
      </Card>
    </div>
  );
}

export function OralBoardGuide() {
  return (
    <div className="grid md:grid-cols-2 gap-3" data-testid="board-guide">
      <Card title="Attire & the day of" icon={Shirt} testid="ob-guide-attire">
        <Bullets items={["A suit or equivalent, pressed and ready the night before.", "Arrive early; bring anything the invitation lists.", "Phone off and out of sight before you walk in."]} />
      </Card>
      <Card title="In the room" icon={HelpCircle} testid="ob-guide-room">
        <Bullets items={["Greet every panelist; eye contact as you answer, not just with the person who asked.", "Answer the question asked, with a real example — situation, what you did, result.", "It's fine to pause and think. Fillers and hedging cost more than a two-second beat."]} />
      </Card>
    </div>
  );
}
